import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message
} from 'antd'
import { GiftOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import api, { getApiErrorMessage } from '../utils/api'
import { useMediaQuery } from 'react-responsive'
import { usePermission } from '../hooks/usePermission'
import { formatTz } from '../utils/timezone'
import ResponsiveSelect from './common/ResponsiveSelect'
import Hint from './common/Hint'

const { Text } = Typography

const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const BalanceValue = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: #3e3d3c;
  line-height: 1.2;

  small {
    font-size: 14px;
    font-weight: 400;
    margin-left: 4px;
    color: #8c8c8c;
  }
`

const TYPE_LABELS = {
  opening: { text: '期初', color: 'default' },
  checkout: { text: '消費', color: 'magenta' },
  topup: { text: '儲值', color: 'gold' },
  manual: { text: '加點', color: 'green' },
  redeem: { text: '兌換', color: 'purple' },
  adjust: { text: '校正', color: 'blue' }
}

const PAGE_SIZE = 10

/**
 * 顧客詳情頁的「紅利點數」分頁：餘額、異動紀錄、加點、兌換、校正。
 *
 * 加點與兌換只能選店家在【優惠模組 → 紅利點數】設定好的項目；
 * 任意數字的校正只給有 points:settings 的人（店主、店長），用來把紙本集點卡轉進來。
 */
const CustomerPointsPanel = ({ businessId, customerId, onChanged }) => {
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const { can } = usePermission()
  const canManage = can('points:manage')
  const canAdjust = can('points:settings')

  const [settings, setSettings] = useState(null)
  const [balance, setBalance] = useState(0)
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  // 'earn' | 'redeem' | 'adjust' | null
  const [modal, setModal] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()

  const base = `/api/businesses/${businessId}/points`

  const fetchData = useCallback(
    async (p = 1) => {
      if (!businessId || !customerId) return
      setLoading(true)
      try {
        const [s, r] = await Promise.all([
          api.get(`${base}/settings`),
          api.get(`${base}/customers/${customerId}`, {
            params: { page: p, pageSize: PAGE_SIZE }
          })
        ])
        setSettings(s.data?.data || null)
        const d = r.data?.data || {}
        setBalance(d.balance || 0)
        setRows(d.transactions || [])
        setTotal(d.pagination?.total || 0)
        setPage(p)
      } catch (e) {
        message.error(getApiErrorMessage(e, '讀取點數紀錄失敗'))
      } finally {
        setLoading(false)
      }
    },
    [base, businessId, customerId]
  )

  useEffect(() => {
    fetchData(1)
  }, [fetchData])

  const earnRules = (settings?.earnRules || []).filter((x) => x.isEnabled)
  const rewards = (settings?.rewards || []).filter((x) => x.isEnabled)

  const openModal = (type) => {
    form.resetFields()
    setModal(type)
  }

  const handleSubmit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      const res = await api.post(
        `${base}/customers/${customerId}/${modal}`,
        values
      )
      message.success(res.data?.message || '已完成')
      setModal(null)
      await fetchData(1)
      onChanged?.(res.data?.data?.balance)
    } catch (e) {
      message.error(getApiErrorMessage(e, '操作失敗'))
    } finally {
      setSubmitting(false)
    }
  }

  const columns = [
    {
      title: '時間',
      dataIndex: 'createdAt',
      width: 150,
      render: (v) => formatTz(v, 'YYYY/MM/DD HH:mm')
    },
    {
      title: '類型',
      dataIndex: 'type',
      width: 80,
      render: (t) => {
        const l = TYPE_LABELS[t] || { text: t }
        return <Tag color={l.color}>{l.text}</Tag>
      }
    },
    {
      title: '內容',
      key: 'detail',
      render: (_, r) => (
        <Space direction='vertical' size={0}>
          <span>{r.itemName || r.note || '—'}</span>
          {r.itemName && r.note && <Text type='secondary'>{r.note}</Text>}
          {r.operator?.name && (
            <Text type='secondary' style={{ fontSize: 12 }}>
              經手：{r.operator.name}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: '點數',
      dataIndex: 'points',
      width: 80,
      align: 'right',
      render: (v) => (
        <Text strong style={{ color: v > 0 ? '#389e0d' : '#cf1322' }}>
          {v > 0 ? `+${v}` : v}
        </Text>
      )
    },
    ...(isMobile
      ? []
      : [
          {
            title: '餘額',
            dataIndex: 'balanceAfter',
            width: 80,
            align: 'right'
          }
        ])
  ]

  const selectedReward = Form.useWatch('rewardId', form)
  const rewardCost = rewards.find((r) => r._id === selectedReward)?.points || 0

  const modalTitle = {
    earn: '加點',
    redeem: '兌換',
    adjust: '校正點數'
  }[modal]

  return (
    <Spin spinning={loading}>
      {settings && !settings.active && (
        <Hint tone='warning' title='紅利點數尚未啟用'>
          結帳與儲值目前不會送點。請到【優惠模組 → 紅利點數】開啟並設定送點規則。
        </Hint>
      )}

      <BalanceRow>
        <BalanceValue>
          {balance.toLocaleString()}
          <small>點</small>
        </BalanceValue>
        {settings?.active && (
          <Space wrap>
            {canManage && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => openModal('earn')}
                disabled={!earnRules.length}
                title={earnRules.length ? '' : '尚未設定加點項目'}
              >
                加點
              </Button>
            )}
            {canManage && (
              <Button
                type='primary'
                icon={<GiftOutlined />}
                onClick={() => openModal('redeem')}
                disabled={!rewards.length}
                title={rewards.length ? '' : '尚未設定兌換項目'}
              >
                兌換
              </Button>
            )}
            {canAdjust && (
              <Button
                icon={<MinusCircleOutlined />}
                onClick={() => openModal('adjust')}
              >
                校正
              </Button>
            )}
          </Space>
        )}
      </BalanceRow>

      {rows.length ? (
        <Table
          size='small'
          rowKey='_id'
          columns={columns}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            simple: isMobile,
            onChange: (p) => fetchData(p)
          }}
          scroll={{ x: isMobile ? undefined : 560 }}
        />
      ) : (
        !loading && <Empty description='尚無點數紀錄' />
      )}

      <Modal
        title={modalTitle}
        open={!!modal}
        onCancel={() => setModal(null)}
        onOk={handleSubmit}
        okText='確認'
        cancelText='取消'
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout='vertical' preserve={false}>
          {modal === 'earn' && (
            <Form.Item
              name='ruleId'
              label='加點項目'
              rules={[{ required: true, message: '請選擇加點項目' }]}
            >
              <ResponsiveSelect
                placeholder='選擇加點項目'
                options={earnRules.map((r) => ({
                  value: r._id,
                  label: `${r.name}（+${r.points} 點）`
                }))}
              />
            </Form.Item>
          )}

          {modal === 'redeem' && (
            <>
              <Form.Item
                name='rewardId'
                label='兌換項目'
                rules={[{ required: true, message: '請選擇兌換項目' }]}
                extra={`目前 ${balance} 點`}
              >
                <ResponsiveSelect
                  placeholder='選擇兌換項目'
                  options={rewards.map((r) => ({
                    value: r._id,
                    disabled: r.points > balance,
                    label: `${r.name}（${r.points} 點）${
                      r.points > balance ? ' · 點數不足' : ''
                    }`
                  }))}
                />
              </Form.Item>
              {rewardCost > 0 && (
                <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
                  {rewards.find((r) => r._id === selectedReward)?.description}
                  {' '}兌換後剩 {balance - rewardCost} 點
                </Text>
              )}
            </>
          )}

          {modal === 'adjust' && (
            <>
              <Form.Item
                name='points'
                label='調整點數'
                extra='正數加點、負數扣點。例：把紙本集點卡的 12 點轉進來就填 12。'
                rules={[
                  { required: true, message: '請輸入點數' },
                  {
                    validator: (_, v) =>
                      v === 0
                        ? Promise.reject(new Error('點數不可為 0'))
                        : Promise.resolve()
                  }
                ]}
              >
                <InputNumber precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          <Form.Item
            name='note'
            label={modal === 'adjust' ? '原因' : '備註（選填）'}
            rules={
              modal === 'adjust'
                ? [{ required: true, whitespace: true, message: '請填寫原因' }]
                : []
            }
          >
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  )
}

export default CustomerPointsPanel
